import {
  updateFamilyTypeParameters,
  type ExpectedRevitDocumentIdentity,
} from '../../api/revit/revitBridge';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import { invalidateFamilySnapshotCache } from '../../lib/revit/familySnapshotCache';
import { addParametersToBoltFamilies } from './familyParameterHostActions';
import type {
  BoltModelingType,
  LoadedBoltFamilyReplacementPlan,
} from './planner';

export async function ensureLoadedFamilyDiscriminator(input: {
  plan: LoadedBoltFamilyReplacementPlan;
  targetObjectType: BoltModelingType;
  objectTypeParameterName: string;
  objectTypeBindingKind: 'type' | 'instance';
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity;
  cacheScopeKey?: string | null;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}): Promise<{ success: boolean; error?: string }> {
  const loadedFamily = input.plan.loadedFamily;
  if (!loadedFamily) {
    return { success: false, error: 'loaded family was not returned by the host' };
  }

  const parameterName = input.objectTypeParameterName.trim();
  if (!parameterName) {
    return { success: false, error: 'The configured object-type discriminator parameter is empty' };
  }

  // The reference family may predate the project's configured discriminator.
  // Stamp that exact parameter so setup and profile checks inspect identical
  // live evidence, including its configured Type/Instance mode.
  const parameterExists = (loadedFamily.types ?? []).some((type) =>
    (type.parameterValues ?? []).some(
      (value) => value.parameterName.trim().toLowerCase() === parameterName.toLowerCase()
    )
  );
  if (!parameterExists) {
    const addResult = await addParametersToBoltFamilies({
      familyIds: [loadedFamily.id],
      targetObjectType: input.targetObjectType,
      parameterNames: [parameterName],
      bindingKind: input.objectTypeBindingKind,
      options: {
        expectedDocumentIdentity: input.expectedDocumentIdentity,
        onProgress: input.onProgress,
      },
    });
    invalidateFamilySnapshotCache(input.cacheScopeKey);
    if (!addResult.success) {
      return {
        success: false,
        error: addResult.error ||
          `Failed to add ${parameterName} to the loaded ${input.targetObjectType} family`,
      };
    }
  }

  const typeNames = (loadedFamily.types ?? [])
    .map((type) => type.name)
    .filter((name): name is string => Boolean(name && name.trim()));
  if (typeNames.length === 0) {
    return {
      success: false,
      error: `Loaded ${input.targetObjectType} family exposes no type to tag with ${parameterName}`,
    };
  }

  const setResult = await updateFamilyTypeParameters(
    typeNames.map((typeName) => ({
      FamilyType: `${loadedFamily.name}:${typeName}`,
      ParameterName: parameterName,
      Value: input.targetObjectType,
    }))
  );
  invalidateFamilySnapshotCache(input.cacheScopeKey);
  if (!setResult.success || (setResult.data?.errorCount ?? 0) > 0) {
    return {
      success: false,
      error: setResult.error || setResult.data?.errors?.join('; ') ||
        `Failed to set ${parameterName} = ${input.targetObjectType} on the loaded family`,
    };
  }

  return { success: true };
}
